import { useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, TENANT_TOKEN_KEY } from '../api/client';
import { showGrowl } from '../ui/growlState';

export function LoginPage() {
  const nav = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [slug, setSlug] = useState('');
  const [org, setOrg] = useState<{ id: string; name: string; slug: string } | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const onLookupOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const data = await apiGet<{ id: string; name: string; slug: string }>(
        `/api/auth/org/${encodeURIComponent(slug.trim().toLowerCase())}`
      );
      setOrg(data);
      setStep(2);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const onLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const data = await apiPost<{
        token: string;
        mustChangePassword?: boolean;
      }>(
        '/api/auth/login',
        {
          organizationSlug: org!.slug,
          email: email.trim().toLowerCase(),
          password: password.normalize('NFKC').trim(),
        },
        undefined,
        {}
      );
      localStorage.setItem(TENANT_TOKEN_KEY, data.token);
      if (data.mustChangePassword) {
        nav('/login/cambiar-contraseña', { replace: true });
      } else {
        showGrowl('Sesión iniciada', 'success');
        nav('/app', { replace: true });
      }
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={wrap}>
      <div style={card}>
        {/* Brand */}
        <div style={brandRow}>
          <img
            src="/logo.png"
            alt=""
            width={44}
            height={44}
            style={logoImg}
          />
          <span style={brandName}>Interactive Talker</span>
        </div>

        {step === 1 ? (
          <>
            <h1 style={title}>¿Cuál es tu empresa?</h1>
            <p style={subtitle}>Ingresá el slug de tu organización para comenzar.</p>

            <form onSubmit={(e) => void onLookupOrg(e)} style={form}>
              <label style={labelBlock}>
                <span style={labelText}>Empresa (slug)</span>
                <input
                  style={input}
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="mi-empresa"
                  autoComplete="organization"
                  required
                />
              </label>
              {err ? <div style={errorBox}>{err}</div> : null}
              <button type="submit" disabled={loading} style={btnPrimary}>
                {loading ? 'Buscando…' : 'Continuar →'}
              </button>
            </form>

            <p style={hint}>¿No sabés tu slug? Preguntale a tu administrador.</p>
          </>
        ) : (
          <>
            <h1 style={title}>Iniciá sesión</h1>
            <p style={subtitle}>Ingresá tus credenciales.</p>

            {/* Org pill */}
            <div style={orgPill}>
              <span style={{ color: '#64748b', fontSize: 12 }}>Empresa</span>
              <span style={{ fontWeight: 700, color: '#0f172a', marginLeft: 8 }}>{org?.name}</span>
              <button
                type="button"
                style={changeBtn}
                onClick={() => { setStep(1); setOrg(null); setErr(''); }}
              >
                Cambiar
              </button>
            </div>

            <form onSubmit={(e) => void onLogin(e)} style={form}>
              <label style={labelBlock}>
                <span style={labelText}>Email</span>
                <input
                  style={input}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
              </label>
              <label style={labelBlock}>
                <span style={labelText}>Contraseña</span>
                <input
                  style={input}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>
              {err ? <div style={errorBox}>{err}</div> : null}
              <button type="submit" disabled={loading} style={btnPrimary}>
                {loading ? 'Entrando…' : 'Entrar'}
              </button>
            </form>
          </>
        )}

        <div style={footer}>
          <span style={hint}>
            ¿Problemas para ingresar? Contactá a tu administrador.
            {import.meta.env.DEV ? (
              <>
                {' · '}
                <Link to="/super/login" style={{ color: '#7c3aed', fontWeight: 600, textDecoration: 'none' }}>
                  Super admin
                </Link>
              </>
            ) : null}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Styles ─── */

const wrap: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: '#ffffff',
};

const card: CSSProperties = {
  width: '100%',
  maxWidth: 400,
};

const brandRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 32,
};

const logoImg: CSSProperties = {
  display: 'block',
  objectFit: 'contain',
  filter:
    'drop-shadow(1px 0 0 #7c3aed) drop-shadow(-1px 0 0 #7c3aed) drop-shadow(0 1px 0 #7c3aed) drop-shadow(0 -1px 0 #7c3aed) drop-shadow(1px 1px 0 #7c3aed) drop-shadow(-1px 1px 0 #7c3aed) drop-shadow(1px -1px 0 #7c3aed) drop-shadow(-1px -1px 0 #7c3aed)',
};

const brandName: CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  color: '#0f172a',
  letterSpacing: '-0.02em',
};

const title: CSSProperties = {
  margin: '0 0 6px',
  fontSize: 24,
  fontWeight: 800,
  letterSpacing: '-0.03em',
  color: '#0f172a',
};

const subtitle: CSSProperties = {
  margin: '0 0 24px',
  color: '#64748b',
  fontSize: 14,
  lineHeight: 1.5,
};

const form: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const labelBlock: CSSProperties = {
  display: 'block',
};

const labelText: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#334155',
  display: 'block',
  marginBottom: 6,
};

const input: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '12px 14px',
  borderRadius: 10,
  border: '1px solid #e2e8f0',
  fontSize: 15,
  outline: 'none',
  background: '#f8fafc',
  fontFamily: 'inherit',
};

const btnPrimary: CSSProperties = {
  width: '100%',
  marginTop: 4,
  padding: '13px 14px',
  borderRadius: 10,
  border: 'none',
  background: '#7c3aed',
  color: '#fff',
  fontWeight: 700,
  fontSize: 15,
  cursor: 'pointer',
  fontFamily: 'inherit',
  letterSpacing: '-0.01em',
};

const orgPill: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid #e2e8f0',
  background: '#f8fafc',
  fontSize: 13,
  marginBottom: 18,
};

const changeBtn: CSSProperties = {
  marginLeft: 'auto',
  border: 'none',
  background: 'transparent',
  color: '#7c3aed',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  padding: 4,
  fontFamily: 'inherit',
};

const errorBox: CSSProperties = {
  border: '1px solid rgba(239,68,68,0.25)',
  background: 'rgba(254,226,226,0.55)',
  color: '#7f1d1d',
  borderRadius: 10,
  padding: '10px 12px',
  fontSize: 13,
  lineHeight: 1.4,
};

const footer: CSSProperties = {
  marginTop: 24,
  paddingTop: 16,
  borderTop: '1px solid #f1f5f9',
};

const hint: CSSProperties = {
  fontSize: 12.5,
  color: '#64748b',
  lineHeight: 1.5,
};
